import { CLOUDINARY_CONFIG } from '@/constants/constants';
import { IEventTypeWrite } from '@/types/apiClient/IEventTypeWrite';
import IEventType from '@/types/IEventType';
import useSubmitOnblur from '@/utils/useSubmitOnBlur';
import useSubmitOnChange from '@/utils/useSubmitOnChange';
import { useToast } from '@chakra-ui/react';
import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface IUseSectionAppearanceArgs {
	service: IEventType;
	onChange: (data: Partial<IEventTypeWrite>) => void;
}

export default function useSectionAppearance(args: IUseSectionAppearanceArgs) {
	const toast = useToast();
	const { t } = useTranslation();
	const form = useForm({
		defaultValues: {
			avatar: args.service.imageUrl || '',
			color: args.service.tags.ttTheme || 'primary',
			layout: args.service.tags.ttLayout || 'week',
			slug: args.service.slug,
			startMode: args.service.tags.ttIntervalView ?? 'start',
			titleFormat: args.service.tags.ttBookingTitleFormat ?? '',
		},
	});

	const onSubmit = form.handleSubmit((values) => {
		args.onChange({
			imageUrl: values.avatar,
			slug: values.slug,
			tags: {
				...args.service.tags,
				ttTheme: values.color,
				ttLayout: values.layout,
				ttIntervalView: values.startMode,
				ttBookingTitleFormat: values.titleFormat,
			},
		});
	});

	const { registerSubmitOnBlur } = useSubmitOnblur(form, onSubmit);
	const { registerSubmitOnChange } = useSubmitOnChange(form, onSubmit);

	function handleChangeImageUrlClick() {
		const widget = (window as any).cloudinary.createUploadWidget(CLOUDINARY_CONFIG, (error: any, result: any) => {
			if (!window.cloudinary) {
				toast({ isClosable: true, title: t('This function is temporarily unavailable.'), status: 'error' });
			}

			if (error) {
				toast({ isClosable: true, title: t('Cannot upload image'), status: 'error' });
			}

			if (!error && result?.event === 'success') {
				form.setValue('avatar', result.info.secure_url);
				args.onChange({ imageUrl: result.info.secure_url });
				toast({ isClosable: true, title: t('Image uploaded'), status: 'success' });
			}
		});

		widget.open();
	}

	function handleDeleteImageClick() {
		form.setValue('avatar', '');
		args.onChange({ imageUrl: '' });
	}

	function handleStartModeChange(e: React.ChangeEvent<HTMLSelectElement>) {
		form.setValue('startMode', e.target.value as 'start' | 'range');
		onSubmit();
	}

	function handleTitleBlur(e: React.ChangeEvent<HTMLTextAreaElement>) {
		form.setValue('titleFormat', e.target.value);
		onSubmit();
	}

	return {
		handleTitleBlur,
		handleStartModeChange,
		handleChangeImageUrlClick,
		handleDeleteImageClick,
		onSubmit,
		registerSubmitOnBlur,
		registerSubmitOnChange,
		t,
		form,
	};
}
